import React, { useState, useEffect } from 'react';
import { Box, Button, Fade, Link, Typography, styled } from '@mui/material';
import CookieIcon from '@mui/icons-material/Cookie';
import { useTranslation } from 'react-i18next';
import { useGeneralInfoStore } from '../stores/GeneralInfoStore';

const StyledBox = styled(Box)(({ theme }) => ({
    position: 'fixed',
    bottom: theme.spacing(3),
    right: '2.5%',
    width: '90%',
    maxWidth: '600px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[10],
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'left',
    textAlign: 'left',
    zIndex: 9999,
}));

const ButtonContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: '16px',
});

const CookieBanner = () => {
    const { t } = useTranslation();
    const { isCookieBannerVisible, setIsCookieBannerVisible } = useGeneralInfoStore();
    const [initialCheck, setInitialCheck] = useState(true);

    useEffect(() => {
        const consent = localStorage.getItem('cookieConsent');
        if (!consent && initialCheck) {
            setIsCookieBannerVisible(true);
            setInitialCheck(false);
        }
    }, [setIsCookieBannerVisible, initialCheck]);

    const handleAccept = () => {
        localStorage.setItem('cookieConsent', 'true');
        setIsCookieBannerVisible(false);
    };

    const handleDeny = () => {
        localStorage.setItem('cookieConsent', 'false');
        setIsCookieBannerVisible(false);
    };

    if (!isCookieBannerVisible) return null;

    return (
        <Fade in={isCookieBannerVisible}>
            <StyledBox>
                <Box display="flex" alignItems="left" mb={2} textAlign="left">
                    <Typography variant="h6" mr={1}>
                        {t('cookies.title', 'Cookie Consent')}
                    </Typography>
                    <CookieIcon sx={{ position: "absolute", top: "1rem", right: "1.5rem", fontSize: 39, color: 'primary.main' }} />
                </Box>
                <Typography variant="h4" mb={2}>
                    {t('cookies.mainText', 'We use cookies on our website. Some are necessary for the website to function, while others help us optimize and continuously improve this website. You can find detailed information about necessary and non-necessary cookies and data protection in our privacy policy and cookie settings.')}
                </Typography>
                <Typography variant="h4" mb={2}>
                    {t('cookies.consentText', 'If you consent to the use of cookies, you can revoke this consent at any time with effect for the future by accessing our cookie settings in the privacy policy and selecting which cookies you do not want to accept. More information in the privacy policy.')}
                </Typography>
                <Typography variant="h4" mb={2}>
                    <Link
                        href="https://ui.city/datenschutz"
                        color="primary"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t('cookies.policyLink', 'Cookie Policy & Privacy')}
                    </Link>
                </Typography>
                <ButtonContainer>
                    <Button
                        variant="outlined"
                        onClick={handleDeny}
                        sx={{
                            px: 3,
                            py: 1,
                            borderRadius: '4px',
                            textTransform: 'none',
                        }}
                    >
                        {t('common.buttons.reject', 'Reject')}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAccept}
                        sx={{
                            px: 3,
                            py: 1,
                            borderRadius: '4px',
                            textTransform: 'none',
                        }}
                    >
                        {t('common.buttons.accept', 'Accept')}
                    </Button>
                </ButtonContainer>
            </StyledBox>
        </Fade>
    );
};

export default CookieBanner;